import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { Card, CardContent, Grid, Skeleton } from '@mui/material';
import {
	CerebroBreadcrumb,
	CerebroIcon,
	CerebroPageContainer,
	CerebroSecondaryButton,
	CerebroText,
} from '../../../shared/components';
import {
	useDataRoomQuery,
	useGetDataRoomUserByUserQuery,
	useLenderTermSheetRevisionsQuery,
} from '../../../shared/hooks';
import { ActiveDataRoomUser, CurrentUser } from '../../../shared/models';
import { DateUtils, EnumUtils } from '../../../shared/utils';
import React from 'react';
import { BankTermSheetForm } from './BankTermSheetForm';

interface LenderTermSheetPageProps {
	dataRoomGuid: string | undefined;
	currentUser: CurrentUser;
}

export const LenderTermSheetPage: React.FC<LenderTermSheetPageProps> = ({ dataRoomGuid, currentUser }) => {
	const dataRoomQuery = useDataRoomQuery(dataRoomGuid);
	const roomUserQuery = useGetDataRoomUserByUserQuery(dataRoomGuid, currentUser.userGuid, true);
	const revisionsQuery = useLenderTermSheetRevisionsQuery(dataRoomGuid);
	const [addSheet, setAddSheet] = React.useState<ActiveDataRoomUser | null>(null);

	const closeAddSheetForm = (): void => {
		setAddSheet(null);
	};

	const hasAcceptedTermSheet = dataRoomQuery.data?.hasAcceptedTermSheet;

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Term Sheets</CerebroBreadcrumb>

			{revisionsQuery.isLoading && roomUserQuery.isLoading && dataRoomQuery.isLoading && (
				<>
					<Skeleton height={200} />
					<Skeleton height={200} />
					<Skeleton height={200} />
				</>
			)}

			{revisionsQuery.isSuccess && roomUserQuery.isSuccess && !hasAcceptedTermSheet && (
				<CerebroSecondaryButton sx={{ marginBottom: 2 }} onClick={() => setAddSheet(roomUserQuery.data)}>
					Upload Term Sheet
				</CerebroSecondaryButton>
			)}

			<Grid container spacing={2} alignItems="stretch">
				{hasAcceptedTermSheet && (
					<CerebroText sx={{ marginTop: 2 }}>
						The borrower has accepted a term sheet and will no longer be accepting new term sheets for this
						deal.
					</CerebroText>
				)}

				{revisionsQuery.isSuccess && revisionsQuery.data.length > 0 && (
					<>
						{revisionsQuery.data?.map(revision => {
							return (
								<Grid key={revision.guid} item xs={12}>
									<Card
										key={revision.guid}
										elevation={3}
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											flexDirection: 'column',
											height: '100%',
											marginBottom: 2,
										}}
									>
										<CardContent sx={{ paddingBottom: '0 !important' }}>
											<Grid container sx={{ marginBottom: 2 }}>
												<Grid item xs={8} sx={{ display: 'flex' }}>
													{revision.isCurrentVersion && (
														<CerebroIcon
															padRight
															sx={theme => ({ color: theme.palette.success.main })}
															title="Current Version"
														>
															<FontAwesomeIcon icon={faStar} size="sm" />
														</CerebroIcon>
													)}
													<CerebroText
														variant="large"
														component="span"
														sx={{ fontWeight: 'bold' }}
													>
														Version #{revision.versionNumber}
													</CerebroText>
												</Grid>
												<Grid item xs={4} sx={{ textAlign: 'right' }}>
													{EnumUtils.GetTermSheetResponse(revision.responseStatus)}
												</Grid>
											</Grid>
											<CerebroText gutterBottom sx={{ marginTop: 1 }}>
												{revision.borrowerNotes}
											</CerebroText>
											<CerebroText gutterBottom sx={{ marginTop: 1 }}>
												Submitted by {revision.submittedByName} on{' '}
												{DateUtils.FormatStringAsShortDate(revision.dateReceived?.toString())}
											</CerebroText>
										</CardContent>
									</Card>
								</Grid>
							);
						})}
					</>
				)}
				{revisionsQuery.isSuccess && revisionsQuery.data?.length == 0 && (
					<CerebroText sx={{ marginTop: 5 }}>
						Currently, you have not submitted a term sheet for this deal. Submitting a term sheet through
						the data room is secure and will only be viewable by the borrower. Once submitted, you will
						receive notifications of feedback provided by the borrower. If needed you can upload a revised
						term sheet.
					</CerebroText>
				)}
			</Grid>

			<BankTermSheetForm
				dataRoomGuid={dataRoomGuid}
				currentUser={currentUser}
				dataRoomUser={addSheet}
				onClose={closeAddSheetForm}
			/>
		</CerebroPageContainer>
	);
};
